import React, { useRef } from "react";
import {
   motion,
   useAnimationFrame,
   useMotionValue,
   useReducedMotion,
   useScroll,
   useSpring,
   useTransform,
   useVelocity,
} from "framer-motion";
import { AnimatedNumber, Asterisk, Em, Reveal, SectionTag } from "./primitives";
import { PLANS } from "../../../util/admin/features";

const wrap = (min, max, v) => {
   const range = max - min;
   return ((((v - min) % range) + range) % range) + min;
};

/* ------------------------------------------------------------------ */
/*  Band met zaaktypes die meeloopt met je scrollsnelheid              */
/* ------------------------------------------------------------------ */

export function MarqueeBand() {
   const reduce = useReducedMotion();
   const baseX = useMotionValue(0);
   const { scrollY } = useScroll();
   const velocity = useSpring(useVelocity(scrollY), { damping: 50, stiffness: 400 });
   const factor = useTransform(velocity, [-2000, 0, 2000], [-4, 0, 4], { clamp: false });
   const skew = useTransform(velocity, [-2500, 0, 2500], [8, 0, -8]);
   const dir = useRef(-1);

   useAnimationFrame((_, delta) => {
      if (reduce) return;
      // naar links lopen; scrol je terug, dan draait de band om (zoals de framer-motion-demo)
      let move = dir.current * 2.2 * (delta / 1000);
      const f = factor.get();
      if (f < 0) dir.current = 1;
      else if (f > 0) dir.current = -1;
      move += move * Math.abs(f);
      baseX.set(baseX.get() + move);
   });
   const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`);

   const words = ["Brasserieën", "Restaurants", "Traiteurs", "Bistro's", "Pizzeria's", "Sushibars", "Wijnbars", "Bakkers"];
   const row = (
      <div className="flex shrink-0 items-center">
         {words.map((w, i) => (
            <span key={w} className="flex items-center">
               <span
                  className={`px-4 text-[clamp(1.6rem,3.4vw,2.9rem)] leading-none tracking-[-0.03em] sm:px-7 ${
                     i % 2 ? "font-editorial italic" : "font-semibold"
                  }`}
               >
                  {w}
               </span>
               <Asterisk className="h-[clamp(0.9rem,1.5vw,1.3rem)] w-[clamp(0.9rem,1.5vw,1.3rem)] animate-spin-slow text-lp-blue" />
            </span>
         ))}
      </div>
   );

   return (
      <section aria-label="Voor elke zaak" className="lp-grain lp-grain-dark relative overflow-hidden bg-lp-ink py-5 text-lp-paper sm:py-7">
         <motion.div style={{ x, skewX: skew }} className="flex w-max whitespace-nowrap will-change-transform">
            {row}
            {row}
         </motion.div>
      </section>
   );
}

/* ------------------------------------------------------------------ */
/*  Manifest: woorden lichten op terwijl je scrollt                    */
/* ------------------------------------------------------------------ */

function ScrollWord({ children, progress, range }) {
   const opacity = useTransform(progress, range, [0.12, 1]);
   const blur = useTransform(progress, range, ["blur(3px)", "blur(0px)"]);
   return (
      <motion.span style={{ opacity, filter: blur }} className="inline-block">
         {children}
      </motion.span>
   );
}

const MANIFEST = [
   ["Bestelplatformen"], ["nemen"], ["al"], ["snel"], ["15", "b"], ["tot", "b"], ["30%", "b"], ["van"], ["elke"], ["bestelling."],
   ["Wij"], ["niet."], ["Je"], ["betaalt"], ["een"], ["vast"], ["bedrag"], ["per"], ["maand,"], ["je"], ["gasten"], ["bestellen"],
   ["bij"], ["jóu,", "em"], ["en"], ["elke"], ["klant"], ["blijft"], ["van"], ["jou.", "em"],
];

export function Manifesto() {
   const ref = useRef(null);
   const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
   const n = MANIFEST.length;

   return (
      <section id="idee" className="lp-grain relative bg-lp-paper px-5 py-28 sm:px-8 sm:py-40">
         <div className="mx-auto max-w-[1400px]">
            <SectionTag>Het idee</SectionTag>
            <p
               ref={ref}
               className="mt-10 max-w-[68rem] text-[clamp(1.9rem,4.4vw,4.2rem)] font-medium leading-[1.08] tracking-[-0.035em] text-lp-ink"
            >
               {MANIFEST.map(([w, kind], i) => {
                  const start = i / n;
                  const word =
                     kind === "em" ? (
                        <Em className="text-lp-blue">{w}</Em>
                     ) : kind === "b" ? (
                        <span className="underline decoration-lp-tomato decoration-[0.08em] underline-offset-[0.12em]">{w}</span>
                     ) : (
                        w
                     );
                  // Echte spaties tussen de woorden, zodat zoekmachines en schermlezers een zin lezen
                  return (
                     <React.Fragment key={i}>
                        <ScrollWord progress={scrollYProgress} range={[start, start + 1 / n]}>
                           {word}
                        </ScrollWord>{" "}
                     </React.Fragment>
                  );
               })}
            </p>

            <div className="mt-20 grid gap-px overflow-hidden rounded-3xl border border-lp-ink/10 bg-lp-ink/10 sm:grid-cols-3">
               {[
                  { v: 0, f: (v) => `${Math.max(0, Math.floor(v))}%`, l: "commissie op je bestellingen", start: 30 },
                  { v: PLANS.basic.price, f: (v) => `€${Math.round(v)}`, l: "per maand, alles inbegrepen om te starten" },
                  { v: 24, f: (v) => `${Math.round(v)}/7`, l: "open voor bestellingen, ook als je keuken dicht is" },
               ].map((s, i) => (
                  <Reveal key={i} delay={i * 0.08} className="bg-lp-paper p-8 sm:p-10">
                     <p className="text-[clamp(3rem,6vw,5.5rem)] font-semibold leading-none tracking-[-0.05em] text-lp-ink">
                        {/* De eindwaarde als tekst: crawlers en schermlezers zien nooit "30%" of "0/7" */}
                        <span className="sr-only">{s.f(s.v)}</span>
                        <span aria-hidden>
                           <CountFrom value={s.v} start={s.start} format={s.f} />
                        </span>
                     </p>
                     <p className="mt-4 max-w-[16rem] text-[15px] leading-snug text-lp-muted">{s.l}</p>
                  </Reveal>
               ))}
            </div>
         </div>
      </section>
   );
}

/* Telt vanaf `start` (standaard 0) naar `value` wanneer het in beeld komt */
function CountFrom({ value, start = 0, format }) {
   if (start === 0) return <AnimatedNumber value={value} fromZero format={format} />;
   return <CountDown value={value} start={start} format={format} />;
}

function CountDown({ value, start, format }) {
   const ref = useRef(null);
   const mv = useSpring(start, { stiffness: 50, damping: 22 });
   const text = useTransform(mv, format);
   return (
      <motion.span
         ref={ref}
         className="tabular-nums"
         onViewportEnter={() => mv.set(value)}
         viewport={{ once: true, margin: "-10% 0px" }}
      >
         {text}
      </motion.span>
   );
}
